import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Send, MoreVertical, Ban, ShieldAlert, Check, CheckCheck, 
  ChevronRight, Home, MapPin, 
  Eye, AlertCircle, Search, MessageSquare, ArrowLeft, Clock,
  XCircle, Trash2 
} from 'lucide-react';
import axiosInstance from '../apis/axiosConfig';
import { getFullImageUrl } from '../utils/url';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const posted = new Date(dateStr);
  const diffMs = now - posted;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return posted.toLocaleDateString();
}

export default function Messages() {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;

  const [searchParams, setSearchParams] = useSearchParams();

  const activeCategory = searchParams.get('category');
  const activeConvId = searchParams.get('conversationId');

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Menu / Modal states
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('Spam');
  const [reportDetails, setReportDetails] = useState('');
  const [reporting, setReporting] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const messagesEndRef = useRef(null);
  const menuRef = useRef(null);

  // Fetch all conversations
  const fetchConversations = async (silent = false) => {
    try {
      if (!silent) setLoadingConvs(true);
      const res = await axiosInstance.get('/messages/conversations');
      setConversations(res.data || []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      if (!silent) setLoadingConvs(false);
    }
  };

  // Fetch messages for active conversation
  const fetchMessages = async (convId, silent = false) => {
    if (!convId) return;
    try {
      if (!silent) setLoadingMsgs(true);
      const res = await axiosInstance.get(`/messages/conversations/${convId}/messages`);
      setMessages(res.data || []);
      window.dispatchEvent(new Event('refresh_unread_count'));
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      if (!silent) setLoadingMsgs(false);
    }
  };

  // Fetch conversations on load and poll
  useEffect(() => {
    fetchConversations();
    const interval = setInterval(() => fetchConversations(true), 10000);
    return () => clearInterval(interval);
  }, []);

  // Fetch messages when active conversation changes
  useEffect(() => {
    if (activeConvId) {
      fetchMessages(activeConvId);
      const interval = setInterval(() => fetchMessages(activeConvId, true), 3000);
      return () => clearInterval(interval);
    } else {
      setMessages([]);
    }
  }, [activeConvId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Click outside listener for menu dropdown
  useEffect(() => {
    const clickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, []);

  // Compute Active Conversation
  const activeConv = conversations.find(c => String(c.id) === String(activeConvId));

  // Determine Category Counts
  const roomConvs = conversations.filter(c => (c.context_type || 'room_listing') === 'room_listing');
  const roomUnread = roomConvs.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  const categories = [
    {
      id: 'room_listing',
      title: 'Room Listing',
      subtitle: 'See all messages related to room listing',
      icon: Home,
      unread: roomUnread,
      totalCount: roomConvs.length,
      bgIcon: 'bg-emerald-50 text-emerald-600',
      badgeBg: 'bg-emerald-600',
      hoverBorder: 'hover:border-emerald-200'
    }
  ];

  // Filter conversations for the active category
  const currentCategoryConvs = conversations.filter(c => {
    const type = c.context_type || 'room_listing';
    return type === activeCategory;
  });

  const filteredCategoryConvs = currentCategoryConvs.filter(c => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = c.recipient?.name?.toLowerCase().includes(q);
    const msgMatch = c.last_message?.toLowerCase().includes(q);
    const listingMatch = c.listing?.title?.toLowerCase().includes(q);
    const taskMatch = c.task?.title?.toLowerCase().includes(q);
    const jobMatch = (c.job?.request_name || c.job?.role)?.toLowerCase().includes(q);
    return nameMatch || msgMatch || listingMatch || taskMatch || jobMatch;
  });

  // Current category config
  const currentCatConfig = categories.find(cat => cat.id === activeCategory);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConvId || sending) return;

    try {
      setSending(true);
      const msgText = newMessage.trim();
      setNewMessage('');

      const res = await axiosInstance.post('/messages/messages', {
        conversation_id: activeConvId,
        message: msgText
      });

      setMessages(prev => [...prev, res.data]);
      fetchConversations(true);
    } catch (err) {
      console.error('Failed to send message:', err);
      alert(err.response?.data?.message || 'Failed to send message. You may be blocked.');
    } finally {
      setSending(false);
    }
  };

  // Accept Conversation Request
  const handleAcceptConversation = async (e, convId) => {
    if (e) e.stopPropagation();
    try {
      await axiosInstance.put(`/messages/conversations/${convId}/accept`);
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, status: 'accepted' } : c));
    } catch (err) {
      console.error('Failed to accept request:', err);
      alert('Failed to accept request. Please try again.');
    }
  };

  // Reject Conversation Request
  const handleRejectConversation = async (e, convId) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure you want to reject this request?')) return;
    try {
      await axiosInstance.put(`/messages/conversations/${convId}/reject`);
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, status: 'rejected' } : c));
    } catch (err) {
      console.error('Failed to reject request:', err);
      alert('Failed to reject request. Please try again.');
    }
  };

  // Delete Conversation
  const handleDeleteConversation = async (e, convId) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this message request?')) return;
    try {
      await axiosInstance.delete(`/messages/conversations/${convId}`);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (String(activeConvId) === String(convId)) {
        setSearchParams({ category: activeCategory || 'room_listing' });
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  // Block user
  const handleBlockUser = async () => {
    if (!activeConv || blocking) return;
    try {
      setBlocking(true);
      await axiosInstance.post('/messages/users/block', {
        blocked_user_id: activeConv.recipient.id
      });
      alert(`Blocked ${activeConv.recipient.name} successfully.`);
      setShowBlockModal(false);
      setMenuOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to block user.');
    } finally {
      setBlocking(false);
    }
  };

  // Report user
  const handleReportUser = async () => {
    if (!activeConv || reporting) return;
    try {
      setReporting(true);
      await axiosInstance.post('/messages/users/report', {
        reported_id: activeConv.recipient.id,
        reason: reportReason,
        details: reportDetails
      });
      alert('Report submitted successfully.');
      setShowReportModal(false);
      setReportDetails('');
      setMenuOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit report.');
    } finally {
      setReporting(false);
    }
  };

  // Helper to get item context title
  const getContextTitle = (conv) => {
    if (conv.context_type === 'task' || conv.task) {
      return conv.task?.title || 'HireMate Task';
    }
    if (conv.context_type === 'job' || conv.job) {
      return conv.job?.request_name || conv.job?.role || 'HireMate Job';
    }
    return conv.listing?.title || 'Room Listing';
  };

  // Helper to get avatar URL
  const getAvatarUrl = (recipient) => {
    if (!recipient?.avatar) {
      return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(recipient?.name || 'User')}`;
    }
    return getFullImageUrl(recipient.avatar);
  };

  return (
    <div className="page-premium-wrapper">
      <div className="page-premium-content p-0" style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        
        {/* ── STEP 1: CATEGORY CARDS VIEW ───────────────────────────── */}
        {!activeCategory && !activeConvId && (
          <div className="p-[14px] sm:p-[24px] md:p-[32px] max-w-4xl w-full mx-auto space-y-5 overflow-hidden">
            <div>
              <h1 className="text-xl sm:text-3xl font-black text-slate-900 tracking-tight mb-1">Messages</h1>
              <p className="text-xs sm:text-sm text-slate-500 font-bold m-0">
                Select a category to view your conversations and incoming enquiries.
              </p>
            </div>

            <div className="flex flex-col gap-4 w-full min-w-0">
              {categories.map(cat => {
                const IconComp = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSearchParams({ category: cat.id })}
                    style={{ padding: '16px 18px' }}
                    className={`w-full min-w-0 max-w-full overflow-hidden bg-white rounded-2xl sm:rounded-3xl sm:!p-6 border border-slate-200 shadow-sm flex items-center justify-between gap-3 sm:gap-4 text-left transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer ${cat.hoverBorder}`}
                  >
                    <div className="flex items-center gap-3 sm:gap-5 min-w-0 flex-1">
                      <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shrink-0 ${cat.bgIcon}`}>
                        <IconComp size={24} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display font-black text-slate-900 text-base sm:text-lg leading-tight m-0 truncate">
                          {cat.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-400 font-semibold mt-1 mb-0 leading-snug whitespace-normal sm:truncate">
                          {cat.subtitle}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                      {cat.unread > 0 ? (
                        <span className={`inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-white font-black text-xs rounded-full ${cat.badgeBg} shadow-sm`}>
                          {cat.unread}
                        </span>
                      ) : cat.totalCount > 0 ? (
                        <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">
                          {cat.totalCount}
                        </span>
                      ) : null}
                      <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 shrink-0">
                        <ChevronRight size={18} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── STEP 2: CATEGORY USER LIST VIEW ───────────────────────── */}
        {activeCategory && !activeConvId && (
          <div className="p-[14px] sm:p-[24px] md:p-[32px] max-w-4xl w-full mx-auto space-y-4 overflow-hidden">
            {/* Header with Back Button */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => setSearchParams({})}
                  className="w-10 h-10 rounded-2xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center justify-center transition-all shadow-sm cursor-pointer shrink-0"
                  title="Back to Categories"
                >
                  <ArrowLeft size={18} />
                </button>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-2xl font-black text-slate-900 leading-tight m-0 truncate">
                    {currentCatConfig?.title || 'Conversations'}
                  </h2>
                  <p className="text-xs text-slate-400 font-bold m-0 mt-0.5">
                    {filteredCategoryConvs.length} {filteredCategoryConvs.length === 1 ? 'conversation' : 'conversations'} found
                  </p>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text" 
                placeholder={`Search ${currentCatConfig?.title || 'users'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-xs sm:text-sm font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 shadow-sm transition-all"
              />
            </div>

            {/* User / Conversation List */}
            <div className="bg-white rounded-3xl border border-slate-150 shadow-sm overflow-hidden divide-y divide-slate-100">
              {loadingConvs ? (
                <div className="p-12 text-center text-slate-400 text-xs font-bold">
                  Loading conversations...
                </div>
              ) : filteredCategoryConvs.length === 0 ? (
                <div className="p-12 text-center space-y-3">
                  <div className="w-14 h-14 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto border border-slate-100">
                    <MessageSquare size={24} />
                  </div>
                  <h4 className="text-sm font-black text-slate-700">No conversations found</h4>
                  <p className="text-xs text-slate-400 font-semibold max-w-xs mx-auto">
                    {searchQuery 
                      ? 'No conversations match your search query.' 
                      : `No incoming or active messages for ${currentCatConfig?.title || 'this category'} yet.`}
                  </p>
                </div>
              ) : (
                filteredCategoryConvs.map(conv => {
                  const isPending = conv.status === 'pending';
                  const isRejected = conv.status === 'rejected';
                  const isIncomingRequest = isPending && conv.initiated_by && String(conv.initiated_by) !== String(user?.id);
                  const isOutgoingRequest = isPending && conv.initiated_by && String(conv.initiated_by) === String(user?.id);
                  const isOutgoingRejected = isRejected && conv.initiated_by && String(conv.initiated_by) === String(user?.id);
                  const isIncomingRejected = isRejected && conv.initiated_by && String(conv.initiated_by) !== String(user?.id);

                  return (
                    <div
                      key={conv.id}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSearchParams({ category: activeCategory, conversationId: conv.id }); } }}
                      onClick={() => setSearchParams({ category: activeCategory, conversationId: conv.id })}
                      className="w-full min-w-0 max-w-full overflow-hidden p-[14px] sm:p-[20px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 text-left hover:bg-slate-50/80 transition-colors group cursor-pointer"
                    >
                      <div className="flex items-center gap-3.5 sm:gap-4 min-w-0 flex-1 w-full">
                        {/* User Avatar */}
                        <div className="relative shrink-0">
                          <img 
                            src={getAvatarUrl(conv.recipient)} 
                            alt={conv.recipient?.name || 'User'} 
                            className="w-12 h-12 rounded-full object-cover ring-2 ring-slate-100 shadow-sm"
                          />
                          {conv.unread_count > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-black text-white ring-2 ring-white">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>

                        {/* Text Details */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <div className="flex items-center gap-2 truncate">
                              <h4 className="font-black text-slate-900 text-sm truncate group-hover:text-indigo-600 transition-colors">
                                {conv.recipient?.name || 'User'}
                              </h4>
                              {isIncomingRequest && (
                                <span className="bg-amber-100 text-amber-800 text-[10px] font-black px-2 py-0.5 rounded-md shrink-0">
                                  New Request
                                </span>
                              )}
                              {isOutgoingRequest && (
                                <span className="bg-slate-100 text-slate-600 text-[10px] font-black px-2 py-0.5 rounded-md shrink-0">
                                  Request Sent
                                </span>
                              )}
                              {isOutgoingRejected && (
                                <span className="bg-rose-100 text-rose-700 text-[10px] font-black px-2 py-0.5 rounded-md shrink-0">
                                  Rejected
                                </span>
                              )}
                              {isIncomingRejected && (
                                <span className="bg-slate-100 text-slate-500 text-[10px] font-black px-2 py-0.5 rounded-md shrink-0">
                                  Declined
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-400 font-bold shrink-0">
                              {timeAgo(conv.last_message_at)}
                            </span>
                          </div>

                          {/* Context Title Pill */}
                          <p className="text-[11px] font-black text-indigo-600 truncate mb-1">
                            {getContextTitle(conv)}
                          </p>

                          {/* Last Message Snippet */}
                          <p className="text-xs text-slate-500 font-medium truncate">
                            {conv.last_message || 'Start messaging...'}
                          </p>
                        </div>
                      </div>

                      {/* Action buttons or arrow */}
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center w-full sm:w-auto justify-end pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                        {isIncomingRequest ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => handleAcceptConversation(e, conv.id)}
                              className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-xl transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                            >
                              <Check size={14} /> Accept Request
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleRejectConversation(e, conv.id)}
                              className="px-3 py-1.5 bg-slate-100 hover:bg-rose-50 hover:text-rose-600 text-slate-600 text-xs font-bold rounded-xl transition-all cursor-pointer border border-transparent hover:border-rose-200"
                            >
                              Reject Request
                            </button>
                          </div>
                        ) : isOutgoingRejected ? (
                          <div className="flex items-center gap-2.5">
                            <div className="px-3.5 py-1.5 bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200 text-rose-700 text-xs font-black rounded-xl flex items-center gap-1.5 shadow-sm">
                              <XCircle size={13} className="text-rose-600" />
                              <span>Rejected</span>
                            </div>
                            <div className="text-slate-300 group-hover:text-slate-500 transition-colors">
                              <ChevronRight size={18} />
                            </div>
                          </div>
                        ) : isOutgoingRequest ? (
                          <div className="flex items-center gap-2.5">
                            <div className="px-3.5 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-blue-700 text-xs font-black rounded-xl flex items-center gap-1.5 shadow-sm">
                              <Clock size={13} className="text-blue-600 animate-pulse" />
                              <span>Request Sent</span>
                            </div>
                            <div className="text-slate-300 group-hover:text-slate-500 transition-colors">
                              <ChevronRight size={18} />
                            </div>
                          </div>
                        ) : isIncomingRejected ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400 italic">Request Rejected</span>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteConversation(e, conv.id)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Delete Conversation"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        ) : (
                          <div className="text-slate-300 group-hover:text-slate-500 transition-colors">
                            <ChevronRight size={18} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── STEP 3: CHAT CONVERSATION VIEW ────────────────────────── */}
        {activeConvId && activeConv && (
          <div className="h-[calc(100vh-80px)] flex flex-col bg-white overflow-hidden text-left">
            
            {/* Top Bar Navigation */}
            <header className="bg-white border-b border-slate-100 px-4 sm:px-6 py-3.5 flex items-center justify-between shrink-0 shadow-sm z-10">
              <div className="flex items-center gap-3 min-w-0">
                <button 
                  onClick={() => setSearchParams({ category: activeCategory || activeConv.context_type || 'room_listing' })}
                  className="p-2 -ml-1 text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
                  title="Back to User List"
                >
                  <ArrowLeft size={20} />
                </button>
                
                <img 
                  src={getAvatarUrl(activeConv.recipient)} 
                  alt={activeConv.recipient?.name || 'User'} 
                  className="w-10 h-10 rounded-full object-cover shadow-sm ring-2 ring-slate-100 shrink-0"
                />
                
                <div className="min-w-0">
                  <h3 className="font-black text-slate-900 text-sm truncate">
                    {activeConv.recipient?.name || 'User'}
                  </h3>
                  <p className="text-[10px] text-slate-400 font-bold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Active now • {getContextTitle(activeConv)}
                  </p>
                </div>
              </div>

              <div className="relative" ref={menuRef}>
                <button 
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-colors cursor-pointer"
                >
                  <MoreVertical size={18} />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-slate-100 rounded-2xl shadow-xl z-50 overflow-hidden py-1 text-left">
                    <button 
                      onClick={() => setShowBlockModal(true)}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-slate-700 hover:text-slate-950 font-bold text-xs flex items-center gap-2 cursor-pointer"
                    >
                      <Ban size={14} className="text-red-500" />
                      Block User
                    </button>
                    <button 
                      onClick={() => setShowReportModal(true)}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-slate-700 hover:text-slate-950 font-bold text-xs flex items-center gap-2 cursor-pointer"
                    >
                      <ShieldAlert size={14} className="text-orange-500" />
                      Report User
                    </button>
                  </div>
                )}
              </div>
            </header>

            {/* Context Header Banner */}
            {activeConv.listing && (
              <section className="bg-slate-50/70 border-b border-slate-100 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0 text-emerald-600">
                    <Home size={18} />
                  </div>
                  <div className="min-w-0 text-left">
                    <h4 className="font-black text-slate-900 text-xs sm:text-sm truncate">{activeConv.listing.title}</h4>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500 font-bold">
                      <span className="text-emerald-600 font-black">${activeConv.listing.price || activeConv.listing.rent}/week</span>
                      <span>•</span>
                      <span>{activeConv.listing.roomType || 'Room'}</span>
                      {activeConv.listing.address && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-0.5"><MapPin size={10} /> {activeConv.listing.address.split(',')[0]}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <Link 
                  to={`/properties/${activeConv.listing.id}`} 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black px-4 py-2 rounded-xl transition-all shadow-sm flex items-center gap-1.5 self-end sm:self-auto cursor-pointer"
                >
                  <Eye size={12} /> View Listing
                </Link>
              </section>
            )}

            {/* Incoming Request Action Banner inside Chat */}
            {activeConv.status === 'pending' && String(activeConv.initiated_by) !== String(user?.id) && (
              <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <AlertCircle size={20} />
                  </div>
                  <div className="text-left">
                    <p className="text-xs sm:text-sm font-black text-amber-900">
                      {activeConv.recipient?.name || 'User'} sent you a message request.
                    </p>
                    <p className="text-[11px] text-amber-700 font-medium mt-0.5">
                      Accept this request to enable continuous conversation and reply.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <button
                    type="button"
                    onClick={(e) => handleAcceptConversation(e, activeConv.id)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
                  >
                    <Check size={14} /> Accept Request
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleRejectConversation(e, activeConv.id)}
                    className="px-3.5 py-2 bg-white hover:bg-rose-50 hover:text-rose-600 border border-slate-200 hover:border-rose-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                  >
                    Reject Request
                  </button>
                </div>
              </div>
            )}

            {/* Outgoing Request Pending Banner inside Chat */}
            {activeConv.status === 'pending' && String(activeConv.initiated_by) === String(user?.id) && (
              <div className="bg-blue-50 border-b border-blue-200 px-4 sm:px-6 py-3 flex items-center gap-3 shrink-0">
                <Clock size={18} className="text-blue-600 shrink-0 animate-pulse" />
                <p className="text-xs text-blue-900 font-bold text-left">
                  Message request sent. Waiting for {activeConv.recipient?.name || 'the user'} to accept before continuous chatting begins.
                </p>
              </div>
            )}

            {/* Outgoing Request Rejected Banner inside Chat */}
            {activeConv.status === 'rejected' && String(activeConv.initiated_by) === String(user?.id) && (
              <div className="bg-rose-50 border-b border-rose-200 px-4 sm:px-6 py-3.5 flex items-center gap-3 shrink-0 text-left">
                <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                  <XCircle size={20} />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-black text-rose-900">
                    Message request was declined/rejected.
                  </p>
                  <p className="text-[11px] text-rose-700 font-medium mt-0.5">
                    You cannot send further messages for this listing.
                  </p>
                </div>
              </div>
            )}

            {/* Messages Stream */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-slate-50/40">
              {loadingMsgs && messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-400 text-xs font-bold">
                  Loading messages...
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 p-8">
                  <MessageSquare size={36} className="text-slate-200 mb-2" />
                  <p className="text-sm font-bold text-slate-600">No messages yet</p>
                  <p className="text-xs mt-1">Say hello to {activeConv.recipient?.name} to start the conversation!</p>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isMine = String(msg.sender_id) === String(user?.id);
                  return (
                    <div 
                      key={msg.id || idx} 
                      className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}
                    >
                      <div 
                        className={`max-w-[85%] sm:max-w-[70%] px-4 py-3 rounded-2xl shadow-sm text-sm font-medium leading-relaxed break-words text-left ${
                          isMine 
                            ? 'bg-indigo-600 text-white rounded-br-none' 
                            : 'bg-white text-slate-800 border border-slate-150 rounded-bl-none'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.message}</p>
                      </div>
                      
                      <div className="flex items-center gap-1 text-[9px] text-slate-400 font-bold mt-1 px-1">
                        <span>{timeAgo(msg.createdAt)}</span>
                        {isMine && (
                          <span>
                            {msg.status === 'seen' || msg.is_read ? (
                              <CheckCheck size={12} className="text-indigo-600" />
                            ) : (
                              <Check size={12} className="text-slate-400" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input Box */}
            <footer className="bg-white border-t border-slate-100 p-3 sm:p-4 shrink-0">
              {activeConv.status === 'rejected' ? (
                <div className="flex items-center justify-center p-3.5 bg-rose-50/50 border border-rose-200 rounded-2xl max-w-4xl mx-auto text-xs font-bold text-rose-700 gap-2">
                  <XCircle size={16} className="text-rose-500 shrink-0" />
                  <span>
                    {String(activeConv.initiated_by) === String(user?.id)
                      ? "This message request was rejected by the user."
                      : "You rejected this message request."}
                  </span>
                </div>
              ) : activeConv.status === 'pending' ? (
                <div className="flex flex-col sm:flex-row items-center justify-between p-3.5 bg-slate-50 border border-slate-200 rounded-2xl max-w-4xl mx-auto gap-3 text-left">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                    <AlertCircle size={16} className="text-amber-500 shrink-0" />
                    <span>
                      {String(activeConv.initiated_by) === String(user?.id)
                        ? "Waiting for user to accept this message request before continuous conversation starts."
                        : "Please accept the message request to reply."}
                    </span>
                  </div>
                  {String(activeConv.initiated_by) !== String(user?.id) && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => handleAcceptConversation(e, activeConv.id)}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
                      >
                        <Check size={14} /> Accept Request
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleRejectConversation(e, activeConv.id)}
                        className="px-3.5 py-2 bg-white hover:bg-rose-50 hover:text-rose-600 border border-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                      >
                        Reject Request
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSendMessage} className="flex gap-2 sm:gap-3 max-w-4xl mx-auto">
                  <input 
                    type="text" 
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    maxLength={1024}
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 text-slate-800 text-xs sm:text-sm font-semibold transition-all shadow-inner"
                  />
                  <button 
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="px-5 sm:px-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl flex items-center justify-center transition-all disabled:opacity-50 shadow-md shrink-0 cursor-pointer font-bold gap-1.5"
                  >
                    <Send size={15} />
                    <span className="hidden sm:inline text-xs font-black">Send</span>
                  </button>
                </form>
              )}
            </footer>
          </div>
        )}

        {/* ── Block User Modal ── */}
        {showBlockModal && activeConv && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-sm w-full shadow-2xl border border-slate-100 text-center">
              <div className="w-14 h-14 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                <AlertCircle size={26} />
              </div>
              <h3 className="text-lg font-black text-slate-900 mb-1">Block {activeConv.recipient.name}?</h3>
              <p className="text-sm text-slate-500 mb-6 font-bold leading-relaxed">
                You will no longer receive any messages from this user. This action is permanent.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowBlockModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleBlockUser}
                  disabled={blocking}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all text-xs shadow-md shadow-red-100 cursor-pointer"
                >
                  {blocking ? 'Blocking...' : 'Yes, Block'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Report User Modal ── */}
        {showReportModal && activeConv && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-slate-100">
              <h3 className="text-lg font-black text-slate-900 mb-2">Report {activeConv.recipient.name}</h3>
              <p className="text-xs text-slate-400 font-bold mb-5">Help us keep the community secure. Select a reason for filing this report.</p>
              
              <div className="space-y-4 text-left">
                <div>
                  <span className="text-xs font-black text-slate-500 mb-2 block uppercase tracking-wider">Reason</span>
                  <div className="grid grid-cols-2 gap-2">
                    {['Spam', 'Scam', 'Harassment', 'Inappropriate behaviour', 'Other'].map(r => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setReportReason(r)}
                        className={`px-3 py-2 text-xs font-bold rounded-xl border text-center transition-all cursor-pointer ${
                          reportReason === r 
                            ? 'border-indigo-600 bg-indigo-50 text-indigo-600 font-black' 
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="text-xs font-black text-slate-500 mb-2 block uppercase tracking-wider">Additional details</span>
                  <textarea
                    rows={4}
                    placeholder="Provide context, links, or descriptors of the incident..."
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-600 text-xs sm:text-sm text-slate-800 font-semibold resize-none shadow-inner"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button 
                    onClick={() => setShowReportModal(false)}
                    className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all text-xs cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleReportUser}
                    disabled={reporting || !reportReason}
                    className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl transition-all text-xs shadow-md shadow-orange-100 cursor-pointer"
                  >
                    {reporting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
